import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Button,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  useDisclosure
} from '@heroui/react';
import { X, MapPin, Clock, User } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const MapView = ({ onClose, category = null, posts = [] }) => {
  const { user } = useAuth();
  const [mapPosts, setMapPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const { isOpen, onOpen, onClose: onModalClose } = useDisclosure();

  // Google Maps API key - you may want to move this to environment variables
  const googleMapsKey = 'AIzaSyCsEuLdHnu5Iyo1xtAe0xg-VGlZ0_xWyhs';

  useEffect(() => {
    // Filter posts that have location data
    const postsWithLocation = posts.filter(post => 
      post.latitude && post.longitude
    );
    setMapPosts(postsWithLocation);
  }, [posts]);

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const postDate = new Date(dateString);
    const diffInMinutes = Math.floor((now - postDate) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'agora mesmo';
    if (diffInMinutes < 60) return `${diffInMinutes} minutos atrás`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} horas atrás`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} dias atrás`;
  };

  const getCategoryColor = (postCategory) => {
    switch (postCategory) {
      case 'Segurança': return 'danger';
      case 'Eventos': return 'warning';
      case 'Geral': return 'primary';
      case 'Compra e Venda': return 'success';
      case 'Votação': return 'secondary';
      default: return 'default';
    }
  };

  const handlePostClick = (post) => {
    setSelectedPost(post);
    onOpen();
  };

  // Generate map URL with proper center coordinates
  const generateMapUrl = () => {
    if (!user?.area) return '';
    
    const baseUrl = 'https://www.google.com/maps/embed/v1/place';
    let center;
    
    // Try to use area_full first (like the old system)
    if (user.area.area_full) {
      center = encodeURIComponent(user.area.area_full);
    } else if (user.area.latitude && user.area.longitude) {
      center = `${user.area.latitude},${user.area.longitude}`;
    } else {
      // Fallback to area name with city and state
      const areaName = user.area.name || '';
      const cityName = user.area.city?.name || '';
      const stateName = user.area.city?.state?.name || '';
      center = encodeURIComponent(`${areaName}, ${cityName}, ${stateName}, Brazil`);
    }
    
    return `${baseUrl}?key=${googleMapsKey}&q=${center}&zoom=14&language=pt-br`;
  };

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b p-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <MapPin className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-xl font-bold">
              {category ? `Mapa - ${category}` : 'Mapa da Vizinhança'}
            </h1>
            <p className="text-sm text-gray-600">
              {user?.area?.area_full || user?.area?.name}
            </p>
          </div>
        </div>
        <Button
          isIconOnly
          variant="light"
          onPress={onClose}
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      <div className="flex h-full">
        {/* Map Container */}
        <div className="flex-1 relative">
          <iframe
            title="Area Map"
            width="100%"
            height="100%"
            frameBorder="0"
            style={{ border: 0 }}
            src={generateMapUrl()}
            allowFullScreen
          />
          
          {/* Overlay for posts with location - This would need custom Google Maps integration */}
          {mapPosts.length > 0 && (
            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg p-2">
              <p className="text-sm font-medium">
                {mapPosts.length} posts com localização
              </p>
            </div>
          )}
        </div>

        {/* Sidebar with posts */}
        {mapPosts.length > 0 && (
          <div className="w-80 bg-gray-50 border-l overflow-y-auto">
            <div className="p-4">
              <h3 className="font-semibold mb-4">Posts com Localização</h3>
              <div className="space-y-3">
                {mapPosts.map((post) => (
                  <Card 
                    key={post._id} 
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    isPressable
                    onPress={() => handlePostClick(post)}
                  >
                    <CardBody className="p-3">
                      <div className="flex items-start justify-between mb-2">
                        <Chip 
                          size="sm" 
                          color={getCategoryColor(post.post_category)}
                          variant="flat"
                        >
                          {post.post_category}
                        </Chip>
                        <div className="flex items-center text-xs text-gray-500">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatTimeAgo(post.created_at)}
                        </div>
                      </div>
                      
                      <h4 className="font-medium text-sm mb-1 line-clamp-2">
                        {post.title || post.text}
                      </h4>
                      
                      <div className="flex items-center text-xs text-gray-500">
                        <User className="w-3 h-3 mr-1" />
                        {post.user?.username}
                      </div>
                      
                      {post.latitude && post.longitude && (
                        <div className="flex items-center text-xs text-gray-400 mt-1">
                          <MapPin className="w-3 h-3 mr-1" />
                          {post.latitude.toFixed(4)}, {post.longitude.toFixed(4)}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Post Detail Modal */}
      <Modal isOpen={isOpen} onClose={onModalClose} size="2xl">
        <ModalContent>
          <ModalHeader>
            <div className="flex items-center space-x-2">
              <Chip 
                size="sm" 
                color={getCategoryColor(selectedPost?.post_category)}
                variant="flat"
              >
                {selectedPost?.post_category}
              </Chip>
              <span>{selectedPost?.title}</span>
            </div>
          </ModalHeader>
          <ModalBody className="pb-6">
            {selectedPost && (
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    {selectedPost.user?.username}
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    {formatTimeAgo(selectedPost.created_at)}
                  </div>
                </div>
                
                {selectedPost.title && (
                  <h3 className="text-lg font-semibold">{selectedPost.title}</h3>
                )}
                
                <p className="text-gray-700 leading-relaxed">
                  {selectedPost.text}
                </p>
                
                {selectedPost.latitude && selectedPost.longitude && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <MapPin className="w-4 h-4 mr-2" />
                      Localização
                    </div>
                    <p className="text-xs text-gray-500">
                      {selectedPost.latitude}, {selectedPost.longitude}
                    </p>
                  </div>
                )}
              </div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

    </div>
  );
};

export default MapView;
